import type { TableDataResponse, ColumnPref } from "./types";

function getCookie(name: string): string {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()!.split(";").shift()!;
  return "";
}

const COLUMN_API = "/api/configuration/column/";

export async function fetchColumnPrefs(
  contentTypeName: string,
): Promise<ColumnPref[]> {
  const url = new URL(COLUMN_API, window.location.origin);
  url.searchParams.set("content_type_name", contentTypeName);

  const res = await fetch(url.toString(), {
    credentials: "same-origin",
    headers: { "X-Requested-With": "XMLHttpRequest" },
  });
  if (!res.ok) return [];
  return res.json();
}

export async function saveColumnPrefs(
  contentTypeName: string,
  columns: ColumnPref[],
): Promise<void> {
  await fetch(COLUMN_API, {
    method: "PUT",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCookie("csrftoken"),
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({ content_type_name: contentTypeName, columns }),
  });
}

export async function fetchTableData(
  apiUrl: string,
  params: {
    page: number;
    pageSize: number;
    search: string;
    ordering: string;
    filter?: string;
  }
): Promise<TableDataResponse> {
  const url = new URL(apiUrl, window.location.origin);

  // Preserve existing GET parameters from the current page URL
  const pageParams = new URLSearchParams(window.location.search);
  pageParams.forEach((value, key) => {
    url.searchParams.set(key, value);
  });

  // Table-specific params override page params
  url.searchParams.set("page", String(params.page));
  url.searchParams.set("page_size", String(params.pageSize));
  if (params.search) url.searchParams.set("search", params.search);
  else url.searchParams.delete("search");
  if (params.ordering) url.searchParams.set("ordering", params.ordering);
  else url.searchParams.delete("ordering");
  if (params.filter) url.searchParams.set("filter", params.filter);
  else url.searchParams.delete("filter");

  const res = await fetch(url.toString(), {
    credentials: "same-origin",
    headers: { "X-Requested-With": "XMLHttpRequest" },
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function submitBulkAction(
  changelistUrl: string,
  actionName: string,
  selectedPks: (string | number)[],
  extraFields?: Record<string, string>,
  selectAcross?: boolean,
): Promise<void> {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = changelistUrl + window.location.search;
  form.style.display = "none";

  const csrfInput = document.createElement("input");
  csrfInput.name = "csrfmiddlewaretoken";
  csrfInput.value = getCookie("csrftoken");
  form.appendChild(csrfInput);

  const actionInput = document.createElement("input");
  actionInput.name = "action";
  actionInput.value = actionName;
  form.appendChild(actionInput);

  const selectAcrossInput = document.createElement("input");
  selectAcrossInput.name = "select_across";
  selectAcrossInput.value = selectAcross ? "1" : "0";
  form.appendChild(selectAcrossInput);

  for (const pk of selectedPks) {
    const input = document.createElement("input");
    input.name = "_selected_action";
    input.value = String(pk);
    form.appendChild(input);
  }

  if (extraFields) {
    for (const [name, value] of Object.entries(extraFields)) {
      const input = document.createElement("input");
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }
  }

  document.body.appendChild(form);
  form.submit();
}
